<style src="./PlayerControls.scss" lang="scss"></style>

<template lang="pug">
  .player-controls(
    :class='classlist'
    v-shortkey='shortkeys'
    v-esc='hideMobileControls'
    v-click-outside='hideMobileControls'
    @shortkey='keymap'
  )
    .player-controls__line(
      v-dragged='rewind'
    )
      .player-controls__progress(
        :class='{ "is-drag": isDrag }'
        :style='`width: ${progress}%`'
      )
      .player-controls__preload(
        :style='`width: ${preload}%`'
      )
    .player-controls__body
      .player-controls__left
        .player-controls__track(
          v-if='track'
        )
          Track(
            :track='track'
            :type='"small"'
          )
        button.player-controls__queue(
          v-if='track'
          @click='toggleQueue'
          :title='$l10n("PLAYBACK_QUEUE")'
        )
          Icon(
            :name='"queue"'
          )
      .player-controls__center
        .player-controls__prev(
          @click='prev'
          :class='{ "is-disabled": prevButtonDisabled }'
        )
          ControlButton(
            type='prev'
            icon='prev'
          )
        .player-controls__toggle(
          @click='toggle'
        )
          ControlButton(
            type='play'
            icon='play'
            v-if='!this.running'
          )
          ControlButton(
            type='pause'
            icon='pause'
            v-else
          )
        .player-controls__next(
          @click='next'
          :class='{ "is-disabled": nextButtonDisabled }'
        )
          ControlButton(
            type='next'
            icon='next'
          )
      .player-controls__right
        button.player-controls__shuffle(
          @click='toggleShuffle'
          :class='shuffle ? "is-enabled" : ""'
        )
          Icon(
            name='shuffle'
          )
        button.player-controls__repeat(
          @click='toggleRepeat'
          :class='repeat != "none" ? "is-enabled" : ""'
        )
          Icon(
            :name='repeat === "track" ? "repeatOne" : "repeat"'
          )
        .player-controls__volume
          Volume
        .player-controls__like
          Like(
            v-if='track && track.likes'
            color='accent'
            size='large'
            :liked='track.likes.liked'
            @like='likeTrack'
            @unlike='unlikeTrack'
          )
        .player-controls__add-to-playlist(
          v-if='track'
        )
          ContextMenu(
            :list='contextMenu'
            mode='addToPlaylist'
          )

        button.player-controls__controls-toggle(
          v-if='track'
          type='button'
          :class='{ "is-active": mobileControlsVisible }'
          @click='toggleMobileControls'
        )
          Icon(
            name='dots'
          )
    .player-controls__mobile-controls(
      v-if='mobileControlsVisible && track'
    )
      MobileControls
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Track from "@/components/Track/Track";
import Icon from "@/components/Icon/Icon";
import ControlButton from "@/components/ControlButton/ControlButton";
import ContextMenu from "@/components/ContextMenu/ContextMenu";
import Volume from "@/components/Volume/Volume";
import Like from "@/components/Like/Like";
import MobileControls from "@/components/MobileControls/MobileControls";

export default {
  name: "PlayerControls",
  data() {
    return {
      isDrag: false,
      mobileControlsVisible: false
    };
  },
  components: {
    Icon,
    Like,
    Track,
    ControlButton,
    ContextMenu,
    Volume,
    MobileControls
  },
  computed: {
    ...mapGetters({
      progress: "player/progress",
      preload: "player/preload",
      running: "player/running",
      paused: "player/paused",
      repeat: "player/repeat",
      shuffle: "player/shuffle",
      tracklist: "queue/tracklist",
      currentTrackId: "queue/currentTrackId",
      tracks: "tracks/tracks",
      queuePosition: "queue/position"
    }),
    contextMenu() {
      return this.$contextMenu.create({
        addToPlaylist: {
          tracks: [this.track.id],
          single: true
        }
      });
    },
    shortkeys() {
      return { space: ["space"], right: ["arrowright"], left: ["arrowleft"] };
    },
    classlist() {
      return { "is-disabled": !this.tracklist.length };
    },
    track() {
      return this.tracks[this.currentTrackId];
    },
    prevButtonDisabled() {
      return (
        (this.queuePosition === "first" || this.queuePosition === "one") &&
        this.repeat === "none" &&
        !this.shuffle
      );
    },
    nextButtonDisabled() {
      return (
        (this.queuePosition === "last" || this.queuePosition === "one") &&
        this.repeat === "none" &&
        !this.shuffle
      );
    }
  },
  methods: {
    ...mapActions({
      toggleQueue: "page/toggleQueue",
      like: "tracks/like",
      unlike: "tracks/unlike"
    }),
    toggle() {
      this.$player.toggle();
    },
    prev() {
      this.$player.prev();
    },
    next() {
      this.$player.next(true);
    },
    rewind(e) {
      const isClick = Boolean(e.last);
      const isDrag = "deltaX" in e;

      this.isDrag = isDrag;

      if (isClick || isDrag) {
        this.$player.rewind(e.clientX / e.el.clientWidth, isDrag);
      }
    },
    toggleShuffle() {
      this.$player.toggleShuffle();
    },
    toggleRepeat() {
      this.$player.toggleRepeat();
    },
    keymap({ srcKey }) {
      switch (srcKey) {
        case "space":
          this.toggle();
          break;
        case "right":
          this.$player.rewind(this.$player.progress / 100 + 0.025);
          break;
        case "left":
          this.$player.rewind(this.$player.progress / 100 - 0.025);
          break;
      }
    },
    likeTrack() {
      this.like(this.track.id);
    },
    unlikeTrack() {
      this.unlike(this.track.id);
    },
    toggleMobileControls() {
      this.mobileControlsVisible = !this.mobileControlsVisible;
    },
    hideMobileControls() {
      this.mobileControlsVisible = false;
    }
  }
};
</script>
